import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject, Observable, of } from 'rxjs';
import { Platform, ToastController } from '@ionic/angular';
import { Storage } from '@ionic/storage';
import { BankIdCollectRequestDTO, BankIdCollectResponseDTO, BankIdOrderResponse, BankIdService, UserDTO } from 'ticket-sdk';
import { UserService } from 'ticket-sdk';
import { map, catchError, take, takeWhile } from 'rxjs/operators';
import * as dayjs from 'dayjs';
import { PushService } from './push.service';
import { Router } from '@angular/router';
import { PhoneNumberAlertService } from './phone-number-alert.service';
import { TermsModalService } from './terms-modal.service';
import { environment } from '@env';
import { RitGrpBankidFrejaAuthService } from '@rit/ngx-bankid-freja-auth';

const TOKEN_KEY = 'auth-token';

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {

  abortTimerSubject: Subject<boolean>;
  authenticationState = new BehaviorSubject(false);
  collectRequest: BankIdCollectRequestDTO;
  collectResponseSubject: Subject<BankIdCollectResponseDTO>;
  orderResponse: BankIdOrderResponse;
  user: UserDTO;

  private usernamePattern = '^([0-9]){6,8}-?([0-9]){4}$';

  constructor(
    private storage: Storage, 
    private plt: Platform,
    private bankIdService: BankIdService,
    private userService: UserService,
    public toastController: ToastController,
    private pushService: PushService,
    private platform: Platform,
    private router: Router,
    private phoneNumberAlertService: PhoneNumberAlertService,
    private termsModalService: TermsModalService,
    private eidAuth: RitGrpBankidFrejaAuthService
    ) { 
    this.plt.ready().then(() => {
      this.checkToken();
    });

    this.authenticationState.subscribe(authenticated => {
      if (authenticated) {
        // Get user
        this.userService.getMe().pipe(take(1))
        .subscribe(user => {
          //If user just logged in
          if (!this.user) {
            this.waitForTermsModalAndDisplayPhoneNumberAlert(user);
          }

          this.user = user;
        }, error => {
          this.logout();
        });
      } else {
        this.user = undefined;
      }
    });
  }

  checkAuthentication(): Observable<boolean> {
    return this.userService.getMe().pipe(
      map(user => !!user),
      catchError(error => {
        this.logout(false);
        return of(false)
      })
    );
  }
 
  checkToken() {
    this.getToken().then(res => {
      if (res) {
        this.authenticationState.next(true);
      }
    })
  }

  deleteUser() {
    const anonymizeDate: Date = dayjs().add(7, 'day').endOf('day').toDate();

    this.userService.anonymizeOwnUser(anonymizeDate).pipe(take(1))
    .subscribe(async user => {
      this.logout();
      const toast = await this.toastController.create({
        message: 'Ditt konto har raderats.',
        color: 'success',
        position: 'top',
        duration: 10000
      });
      toast.present();
    }, async error => {
      const toast = await this.toastController.create({
        message: 'Ett fel uppstod. Prova igen om en stund.',
        color: 'danger',
        position: 'top',
        duration: 5000
      });
      toast.present();
    })
  }

  getToken() : Promise<string> {
    return this.storage.get(TOKEN_KEY);
  }
  
  getUser() : UserDTO {
    return this.user;
  }
  
  logout(redirectToLogin: boolean = true) {
    return this.storage.remove(TOKEN_KEY).then(async () => {
      this.user = undefined;
      this.authenticationState.next(false);

      if (redirectToLogin) {
        this.router.navigateByUrl('/login');
      }
      
      if (this.platform.is('capacitor')) {
        await this.pushService.removeUserToken();
      }
    });
  }
 
  isAuthenticated() {
    return this.authenticationState.value;
  }

  private buildMessageMap(external: boolean) {
    return {
      'outstandingTransaction': external ? 'Starta BankID-appen och skanna QR-koden' : 'Väntar på svar från BankID'
    };
  }

  authenticate(external: boolean) {
    const appChannel = this.plt.is('capacitor') ? 'APP' : 'WEB';

    return this.eidAuth.authOrSign<BankIdCollectResponseDTO>({
      external,
      start: () => this.bankIdService.authenticateNoUsername(),
      collect: (req) => this.bankIdService.collect(req),
      cancel: ({ orderRef, transactionId }) => this.bankIdService.cancel({ orderRef, transactionId }),
      ui: {
        provider: 'bankid',
        titleAction: 'Logga in',
        showModal: true,
        messageMap: this.buildMessageMap(external)
      },
      deeplink: {
        redirect: 'ticketapp://',
      },
      appChannel,
    }).pipe(
      map((collectResponse) => {
        if (collectResponse) {
          if (collectResponse.status === 'complete' && collectResponse.token) {
            this.storage.set(TOKEN_KEY, collectResponse.token).then(() => {
              this.authenticationState.next(true);
            });
          } else if (collectResponse.status === 'unauthorized') {
            this.authenticationState.next(false);
          }
        }
        return collectResponse;
      })
    );
  }

  register(external: boolean) {
    const appChannel = this.plt.is('capacitor') ? 'APP' : 'WEB';

    return this.eidAuth.authOrSign<BankIdCollectResponseDTO>({
      external,
      start: () => this.userService.addUserAccount(),
      collect: (req) => this.userService.collect(req),
      cancel: ({ orderRef, transactionId }) => this.bankIdService.cancel({ orderRef, transactionId }),
      ui: {
        provider: 'bankid',
        titleAction: 'Registrera dig',
        showModal: true,
        messageMap: this.buildMessageMap(external)
      },
      deeplink: {
        redirect: 'ticketapp://',
      },
      appChannel,
    }).pipe(
      map((collectResponse) => {
        if (collectResponse) {
          if (collectResponse.status === 'complete' && collectResponse.token) {
            if ((collectResponse as any).userDTO) {
              this.user = (collectResponse as any).userDTO;
            }
            this.storage.set(TOKEN_KEY, collectResponse.token).then(() => {
              this.authenticationState.next(true);
            });
          } else if (collectResponse.status === 'unauthorized') {
            this.authenticationState.next(false);
          }
        }
        return collectResponse;
      })
    );
  }

  cancel(): Observable<void> {
    return this.eidAuth.cancel();
  }

  getUserNamePattern(): string {
    return this.usernamePattern;
  }

  refreshUser() {
    this.userService.getMe().subscribe(user => {
      this.user = user;
    });
  }

  waitForTermsModalAndDisplayPhoneNumberAlert (user: UserDTO) {
    if (environment.showTerms && this.termsModalService.modalStatus$.value !== 'DISMISSED') {
      //Wait for terms modal to close
      this.termsModalService.modalStatus$.pipe(takeWhile(status => status !== 'DISMISSED', true))
      .subscribe((status) => {
        if (status === 'DISMISSED') {
          this.phoneNumberAlertService.displayAlertIfAppropriate(user)
        }
      });
    }
    else {
      this.phoneNumberAlertService.displayAlertIfAppropriate(user);
    }
  }
}
